import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import emailjs from 'emailjs-com';
import Swal from 'sweetalert2';
import '../Contact/Contact.css';

const Contact = () => {
    const navigate = useNavigate();
    
    // Form state
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        destination: '',
        tripDetails: ''
    });
    
    // Error state
    const [errors, setErrors] = useState({
        fullName: '',
        email: '',
        destination: '',
        tripDetails: ''
    });
    
    // Button state
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Form validity state
    const [isFormValid, setIsFormValid] = useState(false);
    
    // Validate form on data change
    useEffect(() => {
        const isValid = 
            formData.fullName.trim() !== '' && !errors.fullName &&
            formData.email.trim() !== '' && !errors.email &&
            !errors.destination &&
            formData.tripDetails.trim() !== '' && !errors.tripDetails;
            
        setIsFormValid(isValid);
    }, [formData, errors]);
    
    // Handle input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
        
        validateField(name, value);
    };
    
    // Field validation
    const validateField = (name, value) => {
        let error = '';
        
        switch (name) {
            case 'fullName':
                if (value.trim() === '') {
                    error = 'Name is required';
                } else if (!/^[A-Za-z\s]+$/.test(value)) {
                    error = 'Name should contain only alphabets';
                }
                break;
                
            case 'email':
                if (value.trim() === '') {
                    error = 'Email is required';
                } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
                    error = 'Please enter a valid email address';
                }
                break;
                
            case 'destination':
                if (value.trim() !== '' && /\d/.test(value)) {
                    error = 'Destination should not contain numbers';
                }
                break;
                
            case 'tripDetails':
                if (value.trim() === '') {
                    error = 'Please tell us about your trip';
                }
                break;
                
            default:
                break;
        }
        
        setErrors({
            ...errors,
            [name]: error
        });
    };
    
    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Final validation check
        if (!isFormValid) {
            return;
        }
        
        // Disable submit button
        setIsSubmitting(true);
        
        // EmailJS configuration (replace with your actual service ID, template ID, and user ID)
        const serviceId = 'service_src72qi';
        const templateId = 'template_utxpfp5';
        const userId = 'X5UqVmxpTiGClhNfI';
        
        // Prepare template parameters
        const templateParams = {
            from_name: formData.fullName,
            from_email: formData.email,
            destination: formData.destination,
            message: formData.tripDetails
        };
        
        // Send email
        emailjs.send(serviceId, templateId, templateParams, userId)
            .then((response) => {
                console.log('SUCCESS!', response.status, response.text);
                
                // Show success alert
                Swal.fire({
                    title: 'Success!',
                    text: 'Your travel inquiry has been submitted. We will get back to you soon!',
                    icon: 'success',
                    confirmButtonText: 'OK'
                }).then(() => {
                    // Navigate to thank you page
                    navigate('/thankyou');
                });
                
                // Reset form and button state
                setFormData({
                    fullName: '',
                    email: '',
                    destination: '',
                    tripDetails: ''
                });
                setIsSubmitting(false);
            })
            .catch((err) => {
                console.log('FAILED...', err);
                
                // Show error alert
                Swal.fire({
                    title: 'Error!',
                    text: 'Something went wrong. Please try again later.',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
                
                // Re-enable submit button
                setIsSubmitting(false);
            });
    };
    
    const calladmin = () => {
        window.location.href = "tel:02039449931";
    };
    
    return (
        <>
            <div className="post-c">
                <div className="overlay-c">
                    <h1 className="text-c">Contact Us</h1>
                </div>
            </div>

            <div className="text-center p-8">
                <div className='open-hr'>
                    <div className='div-tim2'>
                        <h2 className="text-2xl font-bold mb-4 he">Your Journey, Perfectly Planned</h2>
                        <p className="het">Our trusted advisers are here to help you plan the perfect holiday or business trip with ease. From expert recommendations to personalized support, we ensure a seamless experience.</p>
                        <p className="het">From the first call to your return home, we're with you all the way. Wherever you go, we ensure a smooth and stress-free journey.</p>
                    </div>
                </div>
            </div>

            <div className="contact-container">
                {/* Left Side - Contact Form */}
                <div className="contact-form">
                    <h2 className="form-title">Share Your Travel Plans</h2>
                    <form className="form-group" onSubmit={handleSubmit}>
                        <div>
                            <label className='label-c'>Full Name</label>
                            <input 
                                type="text" 
                                name="fullName"
                                className="input-field" 
                                placeholder="Enter your name" 
                                value={formData.fullName}
                                onChange={handleChange}
                            />
                            {errors.fullName && <p className="error-message">{errors.fullName}</p>}
                        </div>
                        <div>
                            <label className='label-c'>Email Address</label>
                            <input 
                                type="email" 
                                name="email"
                                className="input-field" 
                                placeholder="Enter your email" 
                                value={formData.email}
                                onChange={handleChange}
                            />
                            {errors.email && <p className="error-message">{errors.email}</p>}
                        </div>
                        <div>
                            <label className='label-c'>Preferred Destination</label>
                            <input 
                                type="text" 
                                name="destination"
                                className="input-field" 
                                placeholder="Where would you like to go?" 
                                value={formData.destination}
                                onChange={handleChange}
                            />
                            {errors.destination && <p className="error-message">{errors.destination}</p>}
                        </div>
                        <div>
                            <label className='label-c'>Tell us more about your trip...</label>
                            <textarea 
                                name="tripDetails"
                                className="input-field" 
                                rows="4" 
                                placeholder="Share your travel preferences"
                                value={formData.tripDetails}
                                onChange={handleChange}
                            ></textarea>
                            {errors.tripDetails && <p className="error-message">{errors.tripDetails}</p>}
                        </div>
                        <button 
                            type="submit" 
                            className="submit-button"
                            disabled={isSubmitting || !isFormValid}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit'}
                        </button>
                    </form>
                </div>

                {/* Right Side - Contact Info */}
                <div className="contact-info">
                    <h3 className="info-title">Would you like to connect with an expert via call or arrange an in-person meeting?</h3>
                    <p className="info-number">Address:</p>
                    <p className='contact-address'>124 City Road,
                    <br/>
                    London,EC1V 2NX</p>
                    <p className='fw'>Call our knowledgeable team on</p>
                    <p className="info-number">02039449931</p>
                    <button onClick={calladmin} className="call-button">
                        CALL US
                    </button>
                </div>
            </div>

            <div className='map-site'>
                <h2 className="text-2xl font-bold mb-4 he text-center">
                    Our Location
                </h2>
                <div className="L-l">
                    <iframe 
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d10623.247432647555!2d-0.106978700759122!3d51.52682861287587!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48761ca671a6cfb1%3A0x3adb2930f9039227!2s124%20City%20Rd%2C%20London%20EC1V%202NX%2C%20UK!5e0!3m2!1sen!2sin!4v1746705552122!5m2!1sen!2sin" 
                        width="100%" 
                        height="500" 
                        style={{ border: 0 }} 
                        allowFullScreen="" 
                        loading="lazy" 
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Journey Expert Location"
                    ></iframe>
                </div>
            </div>

            <section className="why-section">
  <h2>Why Book with Journey Experts Ltd?</h2>
  <hr />
  <div className="why-container">
    <div className="why-box">
      <p className="why-heading">Expertise</p>
      <p className="why-text">Expert team with 20+ years in the industry</p>
    </div>
    <div className="why-box">
      <p className="why-heading">Protection</p>
      <p className="why-text">Fully ATOL protected for your financial security</p>
    </div>
    <div className="why-box">
      <p className="why-heading">Trusted</p>
      <p className="why-text">UK-registered and trusted travel provider </p>
    </div>
    <div className="why-box">
      <p className="why-heading">Experience</p>
      <p className="why-text">Over 10 years of proven experience</p>
    </div>
    <div className="why-box">
      <p className="why-heading">Convenience</p>
      <p className="why-text"> Easy and secure online booking</p>
    </div>
  </div>
</section>
        </>
    );
};

export default Contact;